import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  IonItem,
  IonList,
  IonSpinner,
  IonButton,
  IonCheckbox,
  IonSelect,
  IonSelectOption,
  IonSegment,
  IonSegmentButton
} from '@ionic/react';

import { saveCoupon } from '@/actions/coupons';
import { preventDefault, gV } from '@/helpers/events';
import { toDollars, toCents } from '@/helpers/humanize';
import { companyLocationsWithCountsSelector } from '@/selectors/locations';
import ValidatedInput from '@/components/ValidatedInput';

const MIN_CODE_LENGTH = 3;

export default function CouponForm({coupon={}, done=_.noop}) {
  const dispatch = useDispatch();
  const locations = useSelector(companyLocationsWithCountsSelector);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [code, setCode] = useState(coupon.code ?? "");
  const [amountMode, setAmountMode] = useState(coupon.amount_percent > 0 ? "percent" : "cents");
  const [amountDollars, setAmountDollars] = useState(toDollars(coupon.amount_cents));
  const [amountPercent, setAmountPercent] = useState(coupon.amount_percent);
  const [startDate, setStartDate] = useState(coupon.start_date ?? "");
  const [endDate, setEndDate] = useState(coupon.end_date ?? "");
  const [limitTotalUses, setLimitTotalUses] = useState(coupon.limit_total_uses ?? "");
  const [limitPerCustomer, setLimitPerCustomer] = useState(coupon.limit_per_customer ?? "");
  const [onlyFirstTimeCustomers, setOnlyFirstTimeCustomers] = useState(coupon.only_first_time_customers ?? false);
  const [minPrice, setMinPrice] = useState(toDollars(coupon.min_price_cents) ?? "");
  const [locationId, setLocationId] = useState(coupon.location_id ?? "");

  const codeValid = code.length >= MIN_CODE_LENGTH;
  const amountValid = amountMode === 'cents' ? amountDollars > 0 : amountPercent > 0;
  const dateValid = !startDate || !endDate || startDate < endDate;
  const valid = _.every([codeValid, amountValid, dateValid]);

  const submitCoupon = async () => {
    setErrorMessage(null);
    if (!valid) return;

    setSaving(true);
    try {
      const amount = amountMode === 'cents' ? {amountCents: toCents(amountDollars)} : {amountPercent};
      await dispatch(saveCoupon({
        id: coupon.id,
        code,
        ...amount,
        startDate, endDate,
        limitTotalUses, limitPerCustomer, onlyFirstTimeCustomers,
        minPriceCents: toCents(minPrice),
        active: true
      }));
      setSaving(false);
      done(true);
    } catch (e) {
      setSaving(false);
      setErrorMessage(_.get(e, 'response.data.error', e.message));
    }
  };

  return (
    <form onSubmit={preventDefault(submitCoupon)}>
      {errorMessage && <IonItem color="danger">{errorMessage}</IonItem>}
      <IonList lines="none">
        <IonItem lines="none">
          <ValidatedInput label="Code"
            name="code"
            value={code}
            setValue={setCode}
            valid={codeValid}
            helperText="Coupon codes are not case-sensitve"
            errorText="Enter a coupon code"
            required/>
        </IonItem>
        <IonItem lines="none">
          <IonSegment value={amountMode} onIonChange={gV(setAmountMode)} mode="ios" color="success">
            <IonSegmentButton value="cents">Dollars</IonSegmentButton>
            <IonSegmentButton value="percent">Percent</IonSegmentButton>
          </IonSegment>
          <div className="ion-padding"/>
          {amountMode === 'cents' ? (
            <ValidatedInput label="Discount Amount"
              name="amount_dollars"
              type="number"
              value={amountDollars}
              setValue={setAmountDollars}
              valid={amountValid}
              min="0.01"
              step="0.01"
              errorText="Enter an amount"
              required/>
          ) : (
            <ValidatedInput label="Discount Percent"
              name="amount_percent"
              type="number"
              value={amountPercent}
              setValue={setAmountPercent}
              min="1"
              valid={amountValid}
              errorText="Enter a percent"
              required/>
          )}
        </IonItem>
        <IonItem lines="none">
          <ValidatedInput label="Start Date"
            type="date"
            name="start_date"
            value={startDate}
            setValue={setStartDate}
            valid={dateValid}
            helperText="optional"
            errorText="Start date must be before end date" />
          <div className="ion-padding"/>
          <ValidatedInput label="End Date"
            type="date"
            name="end_date"
            value={endDate}
            setValue={setEndDate}
            valid={dateValid}
            helperText="optional" />
        </IonItem>
        <IonItem lines="none">
          <ValidatedInput label="Limit Total Uses"
            type="number"
            name="limit_total_uses"
            value={limitTotalUses}
            setValue={setLimitTotalUses}
            min={1}
            valid={true}
            helperText="recommended" />
          <div className="ion-padding"/>
          <ValidatedInput label="Limit Per Customer"
            type="number"
            name="limit_per_customer"
            value={limitPerCustomer}
            setValue={setLimitPerCustomer}
            min={1}
            valid={true}
            helperText="optional" />
        </IonItem>
        <IonItem>
          <IonCheckbox
            labelPlacement="end"
            className="ion-padding-vertical"
            checked={onlyFirstTimeCustomers}
            onIonChange={gV(setOnlyFirstTimeCustomers)}
          >
            First-time Customers Only
          </IonCheckbox>
        </IonItem>
        <IonItem lines="none">
          <ValidatedInput label="Minimum Load Price ($)"
            type="number"
            name="min_price_cents"
            value={minPrice}
            setValue={setMinPrice}
            min={0}
            step="any"
            valid={true}
            helperText="optional" />
        </IonItem>
        {locations?.length > 1 && (
          <IonItem lines="none">
            <IonSelect label="Restrict to Location"
              labelPlacement="floating"
              name="location"
              value={locationId}
              setValue={setLocationId}
              valid={true}
              helperText="optional"
            >
              {locations.map((location) => (
                <IonSelectOption key={location.uuid} value={location.uuid}>
                  {location.name}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>
        )}
        <div className="ion-padding">
          <IonButton type="submit" expand="block">
            {saving ? <IonSpinner name="dots"/> : "Save Coupon"}
          </IonButton>
        </div>
      </IonList>
    </form>
  );
}
